import type {
  ActivityCode,
  EnrichedAuditEvent,
} from '@eppendorf/vnls-audit-event-client';
import { activityCodeDescriptionMap } from '@eppendorf/vnls-audit-event-client';
import { useTranslation } from 'react-i18next';

import { getLocalizedDateTime } from '$shared/utils/date.utils';

import { PerformedByCell } from '$features/audit-events/components/performed-by/performed-by-cell';
import { AuditEventMetadata } from '$features/audit-events/detail/metadata';
import { InfoItem } from '$features/devices/device-information/info-item';
import { InfoList } from '$features/devices/device-information/info-list';

function typeForCode(
  activityCode: ActivityCode,
): 'platform' | 'notification' | 'deviceEvent' | 'device' | 'user' | 'unknown' {
  if (/^P/.test(activityCode)) {
    return 'platform';
  }
  if (/^N/.test(activityCode)) {
    return 'notification';
  }
  if (/^D\.03/.test(activityCode)) {
    return 'deviceEvent';
  }
  if (/^D/.test(activityCode)) {
    return 'device';
  }
  if (/^U/.test(activityCode)) {
    return 'user';
  }
  return 'unknown';
}

export function AuditEventDetail({ event }: { event: EnrichedAuditEvent }) {
  const { t } = useTranslation();
  return (
    <div>
      <InfoList>
        <InfoList.Title>{t('auditEvents.generalInformation')}</InfoList.Title>
        <InfoList.Content>
          <InfoItem
            label={t('auditEvents.header.timestamp')}
            value={getLocalizedDateTime(event.timestamp)}
          />
          <InfoItem
            label={t('auditEvents.header.type')}
            value={t(`auditEvents.eventType.${typeForCode(event.activityCode)}`)}
          />
          <InfoItem
            label={t('auditEvents.header.activity')}
            value={
              event.activityCodeDescription ||
              activityCodeDescriptionMap[event.activityCode] ||
              t('shared.unknown')
            }
          />
          <InfoItem
            label={t('auditEvents.header.performedBy')}
            value={<PerformedByCell entity={event} forDetail />}
          />
        </InfoList.Content>
      </InfoList>

      <InfoList>
        <InfoList.Title>{t('auditEvents.header.details')}</InfoList.Title>
        <AuditEventMetadata event={event} />
      </InfoList>
    </div>
  );
}
